import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { Action, Category } from "constants/events";
import { Location } from "custom-types/Location";
import { useEventTracker } from "hooks/useEventTracker";
import useUserFulfillmentPreference from "hooks/useUserFulfillmentPreference";
import { updateLocation } from "redux/action-creators/location";
import { getLocationState } from "redux/selectors/location";
import { convertGooglePlaceIdToLocation } from "utils/convertGoogleGeocoderToLocation";

import DeliveryValidationModal from "components/botanic/DeliveryValidationModal";

export const DELIVERY_CTA_TEXT = "shop delivery products";
export const PICKUP_CTA_TEXT = "shop pickup products";

type AddressCaptureModalProps = {
  isVisible: boolean;
  onDismissed: () => void;
  returnFocusRef: React.RefObject<HTMLElement>;
  isDeliveryMode?: boolean;
  eventCategory?: Category;
};

const AddressCaptureModal = ({
  isVisible,
  onDismissed,
  returnFocusRef,
  isDeliveryMode = true,
  eventCategory = Category.deliveryAddressModal,
}: AddressCaptureModalProps) => {
  const userLocation = useSelector(getLocationState);
  const dispatch = useDispatch();
  const { trackEvent } = useEventTracker();
  const { setUserFulfillmentPreference } = useUserFulfillmentPreference();
  const CTA_TEXT = isDeliveryMode ? DELIVERY_CTA_TEXT : PICKUP_CTA_TEXT;

  React.useEffect(() => {
    // FEC TrackImpression always fires even when modal is not visible, so do this instead.
    if (isVisible) {
      trackEvent(eventCategory, Action.impression, CTA_TEXT);
    }
  }, [isVisible]);

  const handleSetAddress = (newAddress: Location) => {
    setNewAddress(newAddress);
    trackEvent(eventCategory, Action.click, CTA_TEXT);
    onDismissed();
  };

  const handleDismiss = () => {
    trackEvent(eventCategory, Action.click, "dismiss");
    onDismissed();
  };

  const setNewAddress = async (newAddress: Location) => {
    const placeId = newAddress?.placeId || "";

    const location = await convertGooglePlaceIdToLocation(placeId);
    const cookieLocation = { ...newAddress, ...location };

    updateLocation(dispatch, cookieLocation);
    if (isDeliveryMode) {
      setUserFulfillmentPreference("delivery");
    }
  };

  return (
    <DeliveryValidationModal
      data-testid="address-capture-modal"
      defaultAddress={userLocation}
      handlePrimaryAction={handleSetAddress}
      onDismissed={handleDismiss}
      primaryActionLabel={CTA_TEXT}
      returnFocusRef={returnFocusRef}
      showModal={isVisible}
      width={500}
      isDeliveryMode={isDeliveryMode}
    />
  );
};

export default AddressCaptureModal;
